import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { FeatherRotateCcw } from "@subframe/core";

import type { CategoryResponseDto } from "@/generated/model/categoryResponseDto";
import type { NurseryItemDto } from "@/generated/model/nurseryItemDto";
import { UNCATEGORISED_CATEGORY_ID } from "@/lib/evidence-hub";

interface EvidenceHubFiltersProps {
  categories: CategoryResponseDto[];
  categoryId?: string;
  onCategoryChange: (categoryId: string | undefined) => void;

  /** Nurseries the user can access; the control is hidden when there is only one. */
  nurseries: NurseryItemDto[];
  selectedNurseryId: string | null;
  onNurseryChange: (nurseryId: string) => void;

  canReset: boolean;
  onReset: () => void;
}

/**
 * The Evidence Hub filter row: Category, Location and Reset.
 *
 * "Location" selects the nursery, which is the page's scope rather than a filter —
 * picking one writes the session-wide selection the top bar switcher also writes.
 * That is why Reset deliberately leaves it alone, matching the Events Hub.
 *
 * Unlike the Events Hub there is no "All categories" option: the hub shows nothing
 * until a category is picked, so an "All" entry would display an empty table.
 * Reset is the way back to the unfiltered prompt. There *is* an "Uncategorised"
 * option, as the Task Manager has — records with no primary category would
 * otherwise be unreachable once filtering drives the page.
 */
export function EvidenceHubFilters({
  categories,
  categoryId,
  onCategoryChange,
  nurseries,
  selectedNurseryId,
  onNurseryChange,
  canReset,
  onReset,
}: EvidenceHubFiltersProps) {
  const showLocation = nurseries.length > 1;

  return (
    <div className="flex w-full flex-wrap items-center gap-3">
      <div data-testid="evidence-filter-category">
        <Select
          placeholder="Select a category"
          // Empty string, not undefined: the underlying Radix Select treats
          // undefined as "uncontrolled" and keeps showing the last selected
          // label, so Reset would clear the list but not this control.
          value={categoryId ?? ""}
          onValueChange={onCategoryChange}
        >
          {categories.map((category) => (
            <Select.Item key={category.id} value={category.id}>
              {category.name}
            </Select.Item>
          ))}
          <Select.Item value={UNCATEGORISED_CATEGORY_ID}>
            Uncategorised
          </Select.Item>
        </Select>
      </div>

      {showLocation && (
        <div data-testid="evidence-filter-location">
          <Select
            placeholder="All locations"
            value={selectedNurseryId ?? undefined}
            onValueChange={onNurseryChange}
          >
            {nurseries.map((nursery) => (
              <Select.Item key={nursery.id} value={nursery.id}>
                {nursery.name}
              </Select.Item>
            ))}
          </Select>
        </div>
      )}

      <Button
        variant="neutral-tertiary"
        icon={<FeatherRotateCcw />}
        disabled={!canReset}
        onClick={onReset}
      >
        Reset filters
      </Button>
    </div>
  );
}
